import {
  Dialog,
  DialogTitle,
  Transition,
  TransitionChild
} from '@headlessui/react'
import { isEmpty } from 'lodash'
import {
  Fragment,
  forwardRef,
  useEffect,
  useImperativeHandle,
  useState
} from 'react'

import { classNames } from '../helpers'

const Modal = forwardRef((props, ref) => {
  const [visible, setVisible] = useState(false)
  const [alertData, setAlertData] = useState({})

  useEffect(() => {
    if (isEmpty(alertData)) return
    setVisible(true)
  }, [alertData])

  useImperativeHandle(ref, () => ({
    alert(title, messages, buttons, iconComponent, children, maxwxl) {
      const toArray = Array.isArray(messages)
        ? messages.filter((message) => message)
        : [messages]

      setAlertData({
        title,
        messages: toArray,
        buttons,
        iconComponent,
        children,
        maxwxl
      })
    }
  }))

  return (
    <Transition show={visible} as={Fragment}>
      <Dialog
        as='div'
        auto-reopen='true'
        className='fixed inset-0 z-20 overflow-y-auto'
        onClose={setVisible}
      >
        <div className='flex min-h-screen items-end justify-center px-4 pb-20 pt-4 text-center sm:block sm:p-0'>
          <TransitionChild
            as={Fragment}
            enter='ease-out duration-300'
            enterFrom='opacity-0'
            enterTo='opacity-100'
            leave='ease-in duration-200'
            leaveFrom='opacity-100'
            leaveTo='opacity-0'
          >
            <div
              className='fixed inset-0 bg-slate-900/25 backdrop-blur-sm transition-opacity'
              aria-hidden='true'
            />
          </TransitionChild>

          {/* This element is to trick the browser into centering the modal contents. */}
          <span
            className='hidden sm:inline-block sm:h-screen sm:align-middle'
            aria-hidden='true'
          >
            &#8203;
          </span>
          <TransitionChild
            as={Fragment}
            enter='ease-out duration-300'
            enterFrom='opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95'
            enterTo='opacity-100 translate-y-0 sm:scale-100'
            leave='ease-in duration-200'
            leaveFrom='opacity-100 translate-y-0 sm:scale-100'
            leaveTo='opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95'
          >
            <div
              className={classNames(
                'inline-block transform rounded-lg bg-white px-4 pb-4 pt-5 text-left align-bottom shadow-xl transition-all sm:my-8 sm:w-full sm:px-16 sm:py-8 sm:align-middle',
                alertData?.maxwxl ? 'sm:max-w-xl' : 'sm:max-w-lg'
              )}
            >
              <div>
                <div className='mx-auto flex items-center justify-center'>
                  {alertData?.iconComponent}
                </div>
                <div className='mt-3 text-center'>
                  <DialogTitle
                    as='h3'
                    className='text-2xl font-bold leading-6 text-gray-900'
                  >
                    {alertData?.title}
                  </DialogTitle>
                  <div className='mt-6 pb-px'>
                    {(alertData?.messages || []).map((message, index) => (
                      <p key={index} className='mb-2 text-sm text-gray-900'>
                        {message}
                      </p>
                    ))}
                  </div>
                </div>
              </div>

              <div>{alertData?.children}</div>

              <div
                className={classNames(
                  alertData?.buttons?.length > 1
                    ? 'sm:grid sm:grid-flow-row-dense sm:grid-cols-2 sm:gap-3'
                    : 'mx-auto w-2/3',
                  'mt-6'
                )}
              >
                {alertData?.buttons?.map(({ onPress, style, text }, index) => (
                  <button
                    key={index}
                    type='button'
                    onClick={() => {
                      onPress && onPress()
                      setVisible(false)
                    }}
                    className={classNames(
                      index === 0 &&
                        alertData?.buttons?.length > 1 &&
                        'sm:col-start-2',
                      index === 1 && 'mt-3 sm:col-start-1',
                      style === 'cancel'
                        ? 'border-gray-300 bg-white text-gray-700 hover:bg-gray-50'
                        : 'border-transparent bg-blue-600 text-white hover:bg-blue-700',
                      style === 'destructive' &&
                        'border-transparent bg-red-600 text-white hover:bg-red-500 focus:ring-red-500',
                      'inline-flex w-full justify-center whitespace-nowrap rounded-md border px-4 py-2 text-base font-medium shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 sm:mt-0 sm:text-sm'
                    )}
                  >
                    {text}
                  </button>
                ))}
              </div>
            </div>
          </TransitionChild>
        </div>
      </Dialog>
    </Transition>
  )
})

// Modal.propTypes = {
//   title: PropTypes.string.isRequired,
//   messages: PropTypes.string.isRequired,
//   buttons: PropTypes.arrayOf(PropTypes.object).isRequired, // TODO defice object containing keys text, onPress, style
//   visible: PropTypes.bool.isRequired,
//   setVisible: PropTypes.func.isRequired,
// };

export default Modal
